import * as React from "react";
import { Button, type ButtonVariant } from "@/components/ui/button";
import { Tooltip } from "@/components/ui/tooltip";

interface IconActionButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Either a component TYPE (`SaltIcons.AddIcon`) or an already-built ELEMENT
   * (`<SaltIcons.AddIcon />`). Both shapes exist in this codebase and neither is
   * wrong: page code passes the type, while `TableToolbarAction.icon` is a
   * `ReactNode` because callers build it conditionally — `/corporates` swaps its
   * Download glyph for a spinning Loader mid-export
   * (`CorporateRegistry.tsx:410-417`). Accepting only the type forced a caller to
   * either wrap it in a throwaway component or hand-roll the button, and DR-32
   * forbids hand-rolling.
   */
  icon: React.ElementType | React.ReactNode;
  label: string;
  /**
   * The full `ButtonVariant`, including `"primary"`.
   *
   * This union used to omit `"primary"`, which made the ACCENTED action
   * unexpressible as an icon: converting a page's one accented button to
   * icon-only would silently fall back to `outline` and take the page from 1
   * accented action to 0 — breaking DR-06 while appearing to satisfy DR-32.
   */
  variant?: ButtonVariant;
  /**
   * Busy state, forwarded to `Button` and on to Salt (`button.tsx:43,52-53`).
   *
   * H-6. DR-32.1 requires the identity icon to stay mounted during a pending
   * action and the busy state to be carried by `loading` — because swapping the
   * glyph for a spinner leaves a phone user a blank grey square, with no tooltip
   * to recover it (Salt's `useTooltip` fires on hover and focus only). But
   * `IconActionButtonProps extends ButtonHTMLAttributes`, which has no `loading`,
   * so the prop was rejected by the TYPES before the `{...props}` spread could
   * forward it — and DR-32 makes this component the only permitted vehicle. The
   * rule therefore mandated something the sanctioned component could not express.
   *
   * This is the same defect as the `variant` narrowing documented above, one prop
   * over: a too-narrow type quietly making a rule unimplementable rather than
   * failing loudly. Salt renders the spinner ALONGSIDE the content, so the icon
   * survives.
   */
  loading?: boolean;
  /** Announced in a live region while `loading`. Ignored otherwise. */
  loadingAnnouncement?: string;
}

/**
 * Render either shape of `icon`.
 *
 * `React.isValidElement` narrows only the TRUE branch, so the element case must be
 * split out before the remaining union — which still includes `undefined` and
 * `string` — is used as a JSX tag. Without the split this is a TS2786.
 */
function renderIcon(icon: React.ElementType | React.ReactNode): React.ReactNode {
  if (React.isValidElement(icon)) {
    return icon;
  }
  const IconComponent = icon as React.ElementType;
  return <IconComponent aria-hidden="true" size={1} />;
}

const IconActionButton = React.forwardRef<HTMLButtonElement, IconActionButtonProps>(
  ({ className, variant = "outline", icon: Icon, label, disabled, style, ...props }, ref) => {
    return (
      <Tooltip content={<p>{label}</p>} delayDuration={200}>
        <span
          aria-label={disabled ? `${label}, unavailable` : undefined}
          style={{ display: "inline-flex" }}
          tabIndex={disabled ? 0 : undefined}
        >
          <Button
            {...props}
            className={className}
            variant={variant}
            ref={ref}
            disabled={disabled}
            style={{
              minHeight: "var(--salt-size-base)",
              minWidth: "var(--salt-size-base)",
              padding: 0,
              ...style,
            }}
          >
            {renderIcon(Icon)}
            <span
              style={{
                position: "absolute",
                width: "1px",
                height: "1px",
                padding: 0,
                margin: "-1px",
                overflow: "hidden",
                clip: "rect(0, 0, 0, 0)",
                whiteSpace: "nowrap",
                border: 0,
              }}
            >
              {label}
            </span>
          </Button>
        </span>
      </Tooltip>
    );
  }
);
IconActionButton.displayName = "IconActionButton";

export { IconActionButton };
